// BrowseHome.jsx
import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { withTranslation } from 'react-i18next';

import SeoHelmetWithTDK from '../component/SeoHelmetWithTDK.jsx';
import BrowseExploreJsonLd from '../component/BrowseExploreJsonLd.jsx';

import Redirect from '../container/Redirect.js';
import HomeSearchBar from '../container/HomeSearchBar.js';

import { getIsIphoneXSeriesPWA } from '../resource/getUserAgent.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';

import media from '../style/media.js';
import {
  footerNavigationHeight,
  headerHeight,
  iPhoneXSeriesFooterPaddingInPWA,
} from '../style/variables.js';

const GAP_DEFAULT_SIZE = 16;

const isIphoneXSeriesPWA = getIsIphoneXSeriesPWA();

const Gap = styled.div`
  margin-bottom: ${({ marginBottom }) =>
    marginBottom || `${GAP_DEFAULT_SIZE}px`};
`;

export const BrowseHome = ({
  token,
  isAuthed,
  followingCount,
  followingPageRequirement,
  shouldRenderSearchBar,
  fetchFollowingCountStatus,
  fetchFollowingTotalCount,
  t,
}) => {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    if (
      isAuthed &&
      null === followingCount &&
      'fetching' !== fetchFollowingCountStatus?.status
    ) {
      fetchFollowingTotalCount();
    }
  }, [
    isAuthed,
    fetchFollowingCountStatus,
    fetchFollowingTotalCount,
    followingCount,
  ]);

  useEffect(() => {
    const nextTick = setTimeout(() => {
      setIsClient(true);
    });

    return () => {
      clearTimeout(nextTick);
    };
  }, []);

  const seo = useMemo(() => {
    return (
      <>
        <SeoHelmetWithTDK
          title={t('seo-explore-title', {
            ns: TranslationNamespace.SEO,
          })}
          description={t('seo-explore-description', {
            ns: TranslationNamespace.SEO,
          })}
        />
        <BrowseExploreJsonLd />
        <HeadingOne>
          {t('seo-explore-h1', {
            ns: TranslationNamespace.SEO,
          })}
        </HeadingOne>
      </>
    );
  }, [t]);

  if (
    isClient &&
    (!token ||
      ('fetched' === fetchFollowingCountStatus?.status &&
        followingCount < followingPageRequirement))
  ) {
    return (
      <BrowseContentWrapper>
        <Redirect to="/explore" />
      </BrowseContentWrapper>
    );
  } else if (isClient && followingCount >= followingPageRequirement) {
    return (
      <BrowseContentWrapper>
        <Redirect to="/following" />
      </BrowseContentWrapper>
    );
  } else {
    return (
      <StyledBrowseHome>
        {seo}
        <Body isIphoneXSeriesPWA={isIphoneXSeriesPWA}>
          {shouldRenderSearchBar && (
            <SearchBarWrapper>
              <HomeSearchBar />
            </SearchBarWrapper>
          )}
        </Body>
      </StyledBrowseHome>
    );
  }
};

BrowseHome.propTypes = {
  token: PropTypes.string,
  isAuthed: PropTypes.bool,
  fetchFollowingCountStatus: PropTypes.object,
  followingCount: PropTypes.number,
  followingPageRequirement: PropTypes.number,
  shouldRenderSearchBar: PropTypes.bool,
  fetchFollowingTotalCount: PropTypes.func,
  t: PropTypes.func.isRequired,
};

BrowseHome.defaultProps = {
  token: '',
  isAuthed: false,
  fetchFollowingCountStatus: null,
  followingCount: null,
  followingPageRequirement: 2,
  shouldRenderSearchBar: false,
  fetchFollowingTotalCount: () => null,
};

const StyledBrowseHome = styled.div`
  position: relative;
  padding-top: ${headerHeight}px;
  min-height: 100vh;
`;

const BrowseContentWrapper = styled.div`
  min-height: 100vh;
`;

const HeadingOne = styled.h1`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  font-size: 16px;
  color: #191919;
`;

const Body = styled.div`
  ${media.mobile`
    padding-bottom: ${({ isIphoneXSeriesPWA }) =>
      footerNavigationHeight +
      (isIphoneXSeriesPWA ? iPhoneXSeriesFooterPaddingInPWA : 0)}px;
  `};
  min-height: 100vh;
`;

const SearchBarWrapper = styled(Gap)`
  padding: 0px 16px;
  & > div {
    display: none;
  }
  ${media.tablet`
    margin: 12px 0;
    & > div {
      display: block;
    }
  `};
  ${media.mobile`
    display: none;
  `};
`;

export default withTranslation()(BrowseHome);
